import React from "react";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";

import { NEUTRAL, RADIUS, VIOLET } from "../styles/brandTokens";

/**
 * Keep this list aligned with `src/cortex/m365_sensitive_actions.py`.
 * Each action has its own mode. A bulk control sets every action at once.
 */
export const M365_SENSITIVE_WRITE_ACTIONS: Array<{ name: string; label: string; group: string }> = [
  { name: "m365_delete_user", label: "Delete a user", group: "Identity" },
  { name: "m365_reset_password", label: "Reset or change a password", group: "Identity" },
  { name: "m365_block_unblock_user", label: "Block or unblock sign-in", group: "Identity" },
  { name: "m365_revoke_user_session", label: "Revoke a user's sessions", group: "Identity" },
  { name: "m365_delete_authentication_method", label: "Remove an MFA or authentication method", group: "Identity" },
  { name: "m365_remove_license_from_user", label: "Remove a license", group: "Identity" },
  { name: "m365_grant_mailbox_permission", label: "Grant access to another mailbox", group: "Mail" },
  { name: "m365_delete_mailbox", label: "Delete a mailbox", group: "Mail" },
  { name: "m365_delete_mail_user", label: "Delete a mail user", group: "Mail" },
  { name: "m365_delete_group", label: "Delete a group", group: "Mail" },
  { name: "m365_delete_team", label: "Delete a team", group: "Tenant" },
  { name: "m365_delete_spo_site", label: "Delete a SharePoint site", group: "Tenant" },
  { name: "m365_delete_transport_rule", label: "Delete a mail flow rule", group: "Tenant" },
  { name: "m365_delete_conditional_access_policy", label: "Delete a conditional access policy", group: "Tenant" },
  { name: "m365_delete_app_registration", label: "Delete an app registration", group: "Tenant" },
  { name: "m365_delete_enterprise_app", label: "Delete an enterprise application", group: "Tenant" },
  { name: "m365_delete_app_secret", label: "Delete an application secret", group: "Tenant" },
  { name: "m365_disable_litigation_hold", label: "Turn off litigation hold", group: "Tenant" },
];

export type M365SensitiveWritesMode = "approval" | "auto";
export type M365SensitiveWriteModes = Record<string, M365SensitiveWritesMode>;

export function defaultM365SensitiveWriteModes(): M365SensitiveWriteModes {
  return Object.fromEntries(M365_SENSITIVE_WRITE_ACTIONS.map((action) => [action.name, "approval"])) as M365SensitiveWriteModes;
}

/** A string is the older whole-list setting. A map sets named actions; anything missing stays approval. */
export function normalizeM365SensitiveWriteModes(value: unknown): M365SensitiveWriteModes {
  if (value === "auto" || value === "approval") {
    return Object.fromEntries(M365_SENSITIVE_WRITE_ACTIONS.map((action) => [action.name, value])) as M365SensitiveWriteModes;
  }
  const record = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  return Object.fromEntries(
    M365_SENSITIVE_WRITE_ACTIONS.map((action) => [action.name, record[action.name] === "auto" ? "auto" : "approval"])
  ) as M365SensitiveWriteModes;
}

export function bulkM365SensitiveWriteMode(modes: M365SensitiveWriteModes): M365SensitiveWritesMode | "mixed" {
  const values = M365_SENSITIVE_WRITE_ACTIONS.map((action) => modes[action.name] ?? "approval");
  if (values.every((mode) => mode === "auto")) return "auto";
  if (values.every((mode) => mode === "approval")) return "approval";
  return "mixed";
}

export function setAllM365SensitiveWrites(mode: M365SensitiveWritesMode): M365SensitiveWriteModes {
  return Object.fromEntries(M365_SENSITIVE_WRITE_ACTIONS.map((action) => [action.name, mode])) as M365SensitiveWriteModes;
}

const toggleSx = {
  flexShrink: 0,
  backgroundColor: NEUTRAL.surface,
  borderRadius: `${RADIUS.control}px`,
  "& .MuiToggleButton-root": {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    px: 1.25,
    py: 0.4,
    lineHeight: 1.3,
    color: NEUTRAL.textSecondary,
    borderColor: NEUTRAL.border,
    "&.Mui-selected": {
      backgroundColor: VIOLET.tint,
      color: VIOLET.theoText,
      borderColor: VIOLET.theo,
      "&:hover": { backgroundColor: VIOLET.wash },
    },
  },
} as const;

const ModeToggle: React.FC<{
  id: string;
  label: string;
  labelWeight?: number;
  value: M365SensitiveWritesMode | "mixed";
  onChange: (mode: M365SensitiveWritesMode) => void;
}> = ({ id, label, labelWeight = 400, value, onChange }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography id={`${id}-label`} component="label" sx={{ fontSize: 12.5, fontWeight: labelWeight, color: NEUTRAL.text, lineHeight: 1.4 }}>
        {label}
      </Typography>
      {value === "mixed" ? (
        <Typography sx={{ fontSize: 11.5, color: NEUTRAL.textMuted, lineHeight: 1.3 }}>Mixed</Typography>
      ) : null}
    </Box>
    <ToggleButtonGroup
      id={id}
      exclusive
      size="small"
      value={value === "mixed" ? null : value}
      aria-labelledby={`${id}-label`}
      onChange={(_event, next: string | null) => {
        if (next === "approval" || next === "auto") onChange(next);
      }}
      sx={toggleSx}
    >
      <ToggleButton value="approval">Require approval</ToggleButton>
      <ToggleButton value="auto">Run automatically</ToggleButton>
    </ToggleButtonGroup>
  </Box>
);

export const M365SensitiveWritesControl: React.FC<{
  value: M365SensitiveWriteModes;
  onChange: (modes: M365SensitiveWriteModes) => void;
}> = ({ value, onChange }) => {
  const modes = normalizeM365SensitiveWriteModes(value);
  const bulk = bulkM365SensitiveWriteMode(modes);
  const groups: Array<[string, typeof M365_SENSITIVE_WRITE_ACTIONS]> = [];
  for (const action of M365_SENSITIVE_WRITE_ACTIONS) {
    const existing = groups.find((group) => group[0] === action.group);
    if (existing) existing[1].push(action);
    else groups.push([action.group, [action]]);
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: `${RADIUS.control}px`,
        border: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.subtle,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: NEUTRAL.text, mb: 0.5 }}>
        Sensitive Microsoft 365 writes
      </Typography>
      <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: NEUTRAL.textSecondary, mb: 1.25 }}>
        Require approval holds that action for a technician, including inside Auto Resolver. Run automatically lets Auto Resolver do it on its own.
      </Typography>
      <ModeToggle
        id="agent-m365-sensitive-all"
        label="All sensitive writes"
        labelWeight={600}
        value={bulk}
        onChange={(mode) => onChange(setAllM365SensitiveWrites(mode))}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1.5 }}>
        {groups.map(([group, actions]) => (
          <Box key={group}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 0.2, color: VIOLET.theoText, mb: 0.75 }}>
              {group}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {actions.map((action) => (
                <ModeToggle
                  key={action.name}
                  id={`m365-sensitive-${action.name}`}
                  label={action.label}
                  value={modes[action.name]}
                  onChange={(mode) => onChange({ ...modes, [action.name]: mode })}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
